import { theme } from "@shared/utils/themeColors";
import { useQuery } from "@tanstack/react-query";
import React, { JSX } from "react";
import { useNavigate } from "react-router-dom";
import SettingsSidebar from "../../../partials/settings/SettingsSidebar";
import { LoadingSpinner } from "../../../shared/components/LoadingSpinner";
import { useProfile } from "../../../shared/hooks/useProfile";
import AuthenticatedLayout from "../../../shared/layouts/AuthenticatedLayout";
import { SubscriptionService } from "../services/subscription.service";

/**
 * Page to display subscription change history
 */
const SubscriptionHistoryPage: React.FC = () => {
  const navigate = useNavigate();
  const { user, isLoading: profileLoading, error: profileError } = useProfile();

  // Fetch subscription history
  const { data: historyItems = [], isLoading: historyLoading } = useQuery({
    queryKey: ["subscriptionHistory"],
    queryFn: () => SubscriptionService.getSubscriptionHistory(),
  });

  const isLoading = profileLoading || historyLoading;

  const handleBackToSettings = () => {
    navigate("/settings/plans");
  };

  // Format change type for display
  const formatChangeType = (changeType: string): string => {
    switch (changeType) {
      case "subscription_created":
        return "Subscription Created";
      case "subscription_updated":
        return "Subscription Updated";
      case "subscription_canceled":
        return "Subscription Canceled";
      case "plan_changed":
        return "Plan Changed";
      case "billing_cycle_changed":
        return "Billing Cycle Changed";
      case "payment_method_updated":
        return "Payment Method Updated";
      default:
        return changeType
          ?.replace(/_/g, " ")
          ?.replace(/\b\w/g, (l) => l.toUpperCase());
    }
  };

  // Get appropriate icon for change type
  const getChangeIcon = (changeType: string): JSX.Element => {
    switch (changeType) {
      case "subscription_created":
      case "plan_changed":
        return (
          <div className="flex items-center justify-center w-8 h-8 bg-green-100 dark:bg-green-900/30 rounded-full">
            <svg
              className="w-4 h-4 text-green-500"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 6v6m0 0v6m0-6h6m-6 0H6"
              />
            </svg>
          </div>
        );
      case "subscription_canceled":
        return (
          <div className="flex items-center justify-center w-8 h-8 bg-red-100 dark:bg-red-900/30 rounded-full">
            <svg
              className="w-4 h-4 text-red-500"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </div>
        );
      case "billing_cycle_changed":
        return (
          <div className="flex items-center justify-center w-8 h-8 bg-blue-100 dark:bg-blue-900/30 rounded-full">
            <svg
              className="w-4 h-4 text-blue-500"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
              />
            </svg>
          </div>
        );
      case "payment_method_updated":
        return (
          <div className="flex items-center justify-center w-8 h-8 bg-purple-100 dark:bg-purple-900/30 rounded-full">
            <svg
              className="w-4 h-4 text-purple-500"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z"
              />
            </svg>
          </div>
        );
      default:
        return (
          <div className="flex items-center justify-center w-8 h-8 bg-gray-100 dark:bg-gray-700 rounded-full">
            <svg
              className="w-4 h-4 text-gray-500"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
          </div>
        );
    }
  };

  // Content for the subscription history
  const renderSubscriptionHistory = () => {
    if (historyItems.length === 0) {
      return (
        <div className="p-8 text-center">
          <p className="text-gray-600 dark:text-gray-300">
            No subscription history available.
          </p>
        </div>
      );
    }

    return (
      <div className="divide-y divide-gray-200 dark:divide-gray-700">
        {historyItems &&
          historyItems?.map((item, index) => (
            <div key={index} className="p-6 flex items-start space-x-4">
              {getChangeIcon(item.changeType)}
              <div className="flex-1">
                <div className="flex items-center justify-between mb-1">
                  <h3 className="text-lg font-medium text-gray-800 dark:text-gray-100">
                    {formatChangeType(item.changeType)}
                  </h3>
                  <span className="text-sm text-gray-500 dark:text-gray-400">
                    {/* {format(item.timestamp.toDate(), "MMM d, yyyy")} */}
                  </span>
                </div>
                <p className="text-gray-600 dark:text-gray-300 mb-2">
                  {item.description}
                </p>
                {item.details && (
                  <div className="mt-2 text-sm text-gray-500 dark:text-gray-400">
                    <div className="bg-gray-50 dark:bg-gray-700 rounded p-3">
                      {Object.entries(item.details).map(([key, value]) => (
                        <div
                          key={key}
                          className="flex items-start mb-1 last:mb-0"
                        >
                          <span className="font-medium mr-2">
                            {key
                              .replace(/_/g, " ")
                              .replace(/\b\w/g, (l) => l.toUpperCase())}
                            :
                          </span>
                          <span>
                            {typeof value === "object"
                              ? JSON.stringify(value)
                              : String(value)}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          ))}
      </div>
    );
  };

  return (
    <AuthenticatedLayout>
      <div className="px-4 sm:px-6 lg:px-8 py-8 w-full max-w-9xl mx-auto">
        {/* Page header */}
        <div className="mb-8">
          {/* Title */}
          <h1
            className={`text-2xl md:text-3xl ${theme.text.primary} font-bold`}
          >
            Account Settings
          </h1>
        </div>

        {/* Content */}
        <div className={`${theme.bg.primary} shadow-sm rounded-xl mb-8`}>
          <div className="flex flex-col md:flex-row md:-mr-px">
            <SettingsSidebar />
            <div className="grow">
              {isLoading ? (
                <div className="flex items-center justify-center w-full p-8">
                  <LoadingSpinner />
                </div>
              ) : profileError ? (
                <div className={`p-8 text-center ${theme.text.error}`}>
                  Error loading user data. Please try again.
                </div>
              ) : (
                <div className="p-6">
                  <div className="flex items-center justify-between mb-6">
                    <h2 className={`text-2xl ${theme.text.primary} font-bold`}>
                      Subscription History
                    </h2>
                    <button
                      onClick={handleBackToSettings}
                      className={`btn ${theme.border.primary} hover:border-gray-300 dark:hover:border-gray-600 text-gray-800 dark:text-gray-300`}
                    >
                      Back to Subscription
                    </button>
                  </div>
                  <div
                    className={`${theme.bg.primary} rounded-lg shadow-sm overflow-hidden`}
                  >
                    {renderSubscriptionHistory()}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </AuthenticatedLayout>
  );
};

export default SubscriptionHistoryPage;
